import { LabelValuePair } from "../../../global/formFields/staticSelect/LabelValuePair";
import { PaginationRequestParam } from "../api";
import { RequestVariant } from "../../organization/companies/detail/salarytype";
import { ChangesResponse, WorkingStatus } from "../employees/workContract";
import { DocumentStatus } from "../employees/absence";
import { TimetrackingRequestDto } from "../employees/workSchedule";
import {
  faBabyCarriage,
  faChild,
  faHome,
  faMoneyBill,
  faPlane,
  faSchool,
  faTreePalm,
  faUserNurse,
} from "@fortawesome/pro-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { ApprovalStatus } from "../employees/approval-statuses";

export type OrgChartElement = {
  uuid: string;
  parentCompany: LabelValuePair | null;
  name: string;
  manager: EmployeeAvatarDto[] | null;
  children: OrgChartElement[];
  activeEmployees: number;
};

export interface CompanyView {
  uuid: string;
  city: string;
  manager?: EmployeeAvatarDto[];
  imgLink: string;
  name: string;
  parentCompany?: LabelValuePair;
  plz: string;
  street: string;
}

export interface DocumentTemplateDto {
  documentCategory: DocumentCategoryDto;
  creationDate: string;
  uuid: string;
  companyUuid: string;
  documentCategoryUuid: string;
  documentCategoryDto?: string;
  name: string;
  templateHtml: string;
  content: string;
  updatedAt?: string;
}

export enum AbsenceIcon {
  PLANE = "PLANE",
  NURSE = "NURSE",
  CHILD = "CHILD",
  SCHOOL = "SCHOOL",
  MONEY_BILL = "MONEY_BILL",
  TREE_PALM = "TREE_PALM",
  BABY_CARRIAGE = "BABY_CARRIAGE",
  HOME = "HOME",
}

export const AbsenceIconMap: Record<
  AbsenceIcon,
  { icon: IconProp; label: string }
> = {
  [AbsenceIcon.BABY_CARRIAGE]: { icon: faBabyCarriage, label: "Kinderwagen" },
  [AbsenceIcon.CHILD]: { icon: faChild, label: "Kind" },
  [AbsenceIcon.MONEY_BILL]: { icon: faMoneyBill, label: "Zahlung" },
  [AbsenceIcon.NURSE]: { icon: faUserNurse, label: "Arzt / Ärztin" },
  [AbsenceIcon.SCHOOL]: { icon: faSchool, label: "Schule" },
  [AbsenceIcon.PLANE]: { icon: faPlane, label: "Flugzeug" },
  [AbsenceIcon.TREE_PALM]: { icon: faTreePalm, label: "Palme" },
  [AbsenceIcon.HOME]: { icon: faHome, label: "Zuhause" },
};

export interface AbsencetypeDto {
  uuid: string;
  displayName: string;
  variant: RequestVariant;
  icon?: AbsenceIcon;
  approvalRequired: boolean;
  documentProof: DocumentProof;
  onlyOnWorkingDays: boolean;
  halfDayPossible: boolean;
  overWriteable: boolean;
  budgetRequired: boolean;
  renewalMonth?: number;
  renewalDay?: number;
  payrollRules?: AbsencePayrollRuleView[];
  working: boolean;
  paid: boolean;
  mergeable: boolean;
}

export enum DocumentProof {
  NOT_REQUIRED = "NOT_REQUIRED",
  CUSTOM = "CUSTOM",
  ELECTRONIC_CERTIFICATE = "ELECTRONIC_CERTIFICATE",
}

export interface AbsencePayrollRuleView {
  uuid: string;
  documentStatusCondition: DocumentStatus;
  approvalStatusCondition: ApprovalStatus;
  add: boolean;
  salarytype: SalarytypeDto;
  absencetype: AbsencetypeDto;
}

export interface AbsencePayrollRuleRequest {
  documentStatusCondition: DocumentStatus;
  approvalStatusCondition: ApprovalStatus;
  add: boolean;
  salarytypeId: number;
  absencetypeId: number;
}

export interface AbsenceCalendarLink {
  uuid: string;
  secureId: string;
  companyUuid: string;
  showAbsenceTypeNames: boolean;
  locationUuids: Array<string>;
  locations: Array<LabelValuePair<string>>;
  departmentUuids: Array<string>;
  departments: Array<LabelValuePair<string>>;
  absenceTypeUuids: Array<string>;
  absenceTypes: Array<LabelValuePair<string>>;
  approvalStatuses: Array<ApprovalStatus>;
  documentStatuses: Array<DocumentStatus>;
  includeHolidays: boolean;
  url: string;
  creationDate: string;
}

export enum Role {
  WORK_CONTRACT_ACCESS = "WORK_CONTRACT_ACCESS",
  COMPANY_ACCESS = "COMPANY_ACCESS",
  DEPARTMENT_ACCESS = "DEPARTMENT_ACCESS",
  ALL = "ALL",
}

export interface DocumentCategoryDto {
  uuid: string;
  categoryName: string;
  departmentAccess: boolean;
  contractAccess: boolean;
  company: LabelValuePair;
  variant: RequestVariant;
}

export interface InventoryRequest {
  manufacturer: string;
  price: number;
  inventoryCategory: InventoryCategory;
  priceNew: number;
  privateUse: boolean;
  description: string;
  serialNumber: string;
  condition: InventoryCondition;
  comment: string;
  daysToReturn: number;
  handOutDate: string;
  actualReturnDate: string;
  contractId: string;
}

export enum InventoryCondition {
  NEW = "NEW",
  USED = "USED",
}

export enum InventoryCategory {
  GENERAL = "GENERAL",
  CARS = "CARS",
  TRANSPONDER = "TRANSPONDER",
}

export enum InventoryStatuses {
  IN_STOCK = "IN_STOCK",
  HANDED_OUT = "HANDED_OUT",
  SOON = "SOON",
  OVERDUE = "OVERDUE",
}

export interface InventoryView {
  uuid: string;
  manufacturer: string;
  price: string;
  priceNew: string;
  privateUse: boolean;
  inventoryCategory: InventoryCategory;
  description: string;
  serialNumber: string;
  condition: InventoryCondition;
  comment: string;
  assignedTo: EmployeeAvatarDto;
  status: InventoryStatuses;
  handOutDate: string;
  contractualReturnDate: string;
  actualReturnDate: string;
  daysToReturn: number;
}

export interface CompanyRequest {
  uuid: string;
  name: string;
  street: string;
  plz: string;
  city: string;
  entityType: string;
  manager: string[];
  parentCompanyId: string;
}

export interface EmployeeAvatarDto {
  uuid: string;
  contractId: string;
  firstName: string;
  lastName: string;
  position: string;
  imgLink: string;
  missingData: boolean;
  startDate: string;
  endDate?: string;
  personalNr?: string;
  department: LabelValuePair<string>;
  working?: WorkingStatus;
  workingTimetrackingRequest?: TimetrackingRequestDto;
  missingEmployeeData: boolean;
}

export interface BasicDataView {
  uuid: string;
  name: string;
  street: string;
  plz: string;
  city: string;
  entityType: string;
  imgLink: string;
  manager: EmployeeAvatarDto[];
  parentCompany: LabelValuePair;
}

export interface BerufsgenossenschaftenDto {
  uuid: string;
  berufsGenossenschaft: string;
  mitgliedsNummer: string;
  pin: string;
  industry: string;
}

export interface SocialDataDto {
  uuid: string;
  betriebsNummer: string;
  umlage: boolean;
  sofortmeldepflicht: boolean;
}

export interface TaxDataDto {
  uuid: string;
  financialAuthority: string;
  taxId: string;
  financialAuthoritySepa: boolean;
  financialAuthorityNumber: number;
  iban: string;
  bic: string;
  changes: ChangesResponse[];
}

export interface CompanyHeaderResponse {
  uuid: string;
  name: string;
  numberEmployees: number;
  imgLink: string | null;
  backgroundImgLink: string | null;
  manager: EmployeeAvatarDto[];
  city: string;
}

export interface PayrollDto {
  uuid: string;
  newFounded: boolean;
  firstPayroll: string;
  taxInterval: string;
  cuttingRule: string;
}

export interface SalarytypeDto {
  uuid: string;
  name: string;
  variant: RequestVariant;
  taxRate: number;
  salaryNumber: number;
  st: Art;
  sv: Art;
  gesamtBrutto: boolean;
  pfaendbarkeit: Pfaendbarkeit;
  faktor: Faktor;
  faktorWert: number;
  einheiten: number;
}

export enum Art {
  LAUFEND = "LAUFEND",
  SONSTIG = "SONSTIG",
  PAUSCHAL_AG = "PAUSCHAL_AG",
  PAUSCHAL_AN = "PAUSCHAL_AN",
  MEHRJAEHRIG = "MEHRJAEHRIG",
  FREI = "FREI",
  EINMALBEZUG = "EINMALBEZUG",
  ABFINDUNG = "ABFINDUNG",
  NACHBERECHNUNG = "NACHBERECHNUNG",
  VORJAHR = "VORJAHR",
  ENTGELTGUTHABEN = "ENTGELTGUTHABEN",
}

export enum Pfaendbarkeit {
  NICHT_ZULAESSIG = "NICHT_ZULAESSIG",
  VOLL_PFAENDBAR = "VOLL_PFAENDBAR",
  HALBER_UNTERHALT_3_4 = "HALBER_UNTERHALT_3_4",
  NICHT_NICHT = "NICHT_NICHT",
  NICHT_UNTERHALT_HALB = "NICHT_UNTERHALT_HALB",
}

export enum Faktor {
  STUNDEN = "STUNDEN",
  TAGE = "TAGE",
  KILOMETER = "KILOMETER",
  STUECK = "STUECK",
  OHNE_FAKTOR = "OHNE_FAKTOR",
}

export interface CompanyRelationCheckResponse {
  deletable: boolean;
  workContractCnt: number;
  departmentCnt: number;
  childRelationCnt: number;
  locationCnt: number;
}

export interface SalaryTypeRequestObject {
  uuid: string;
  requestParameter: SalarytypeRequestParam;
}

export interface SalarytypeRequestParam extends PaginationRequestParam {
  variant?: RequestVariant;
}

export interface AbsenceRequestObject {
  uuid: string;
  requestParameter: AbsenceTypeRequestParam;
}

export interface AbsenceTypeRequestParam extends PaginationRequestParam {
  variant?: RequestVariant;
  requiresBudget?: boolean;
}

export interface DocumentTemplatesRequestParam extends PaginationRequestParam {
  documentCategoryUuids?: string | null;
}

export interface InventoryRequestParam extends PaginationRequestParam {
  category?: string;
}
